import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, GraduationCap, Calendar, Mail, Phone, User, MapPin, ArrowLeft, ShieldCheck, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fileUrl } from "@/components/Certificate";
import { SkeletonInstitutionProfile } from "@/components/PageSkeleton";
import { toast } from "sonner";

export default function InstitutionProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/institutions/by-slug/${slug}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e.response?.data?.detail) || "Not found"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <PublicHeader />
        <SkeletonInstitutionProfile />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicHeader />
        <div className="w-full max-w-5xl mx-auto px-6 md:px-12 py-20 text-center">
          <h1 className="font-display font-extrabold text-3xl text-slate-900" data-testid="inst-not-found">Institution not found</h1>
          <p className="mt-2 text-slate-600">The institution you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <Link to="/" className="inline-block mt-6"><Button variant="outline">Back to home</Button></Link>
        </div>
      </div>
    );
  }

  const { institution: inst, degree_breakdown, graduates_by_year } = data;
  const maxYear = Math.max(1, ...graduates_by_year.map((y) => y[1]));
  const maxDegree = Math.max(1, ...degree_breakdown.map((d) => d.count));

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="relative w-full max-w-6xl mx-auto px-6 md:px-12 pt-10 pb-16">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-8" data-testid="profile-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to registry
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start"
          >
            <div className="md:col-span-8">
              <div className="flex items-center gap-5 mb-6">
                {inst.logo_path ? (
                  <img src={fileUrl(inst.logo_path)} alt="" className="w-20 h-20 object-contain rounded-md border border-slate-200 bg-white p-2" data-testid="profile-logo" />
                ) : (
                  <div className="w-20 h-20 rounded-md bg-slate-900 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-blue-400" strokeWidth={2.5} />
                  </div>
                )}
                <div>
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200 mb-2">
                    <ShieldCheck className="w-3 h-3 mr-1" /> Verified partner
                  </Badge>
                  <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight text-slate-900 leading-tight" data-testid="profile-name">
                    {inst.name}
                  </h1>
                </div>
              </div>
              <p className="text-lg text-slate-600 max-w-2xl">
                Est. {inst.year_established} · Currently issuing tamper-proof digital credentials via CertiSeal.
              </p>
            </div>
            <div className="md:col-span-4">
              <Card className="p-6 border-slate-200" data-testid="profile-stats-card">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Verified graduates</div>
                <div className="mt-2 font-display font-extrabold text-6xl text-slate-900 tracking-tight" data-testid="profile-graduate-count">
                  {inst.graduate_count}
                </div>
                <Button onClick={() => navigate("/")} className="mt-6 w-full bg-blue-600 hover:bg-blue-700 font-display font-semibold" data-testid="profile-verify-btn">
                  Verify a credential <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body grid */}
      <section className="py-16 md:py-20">
        <div className="w-full max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Info panel */}
          <Card className="p-8 md:col-span-5 border-slate-200" data-testid="profile-info-panel">
            <h2 className="font-display font-bold text-xl text-slate-900 mb-6">Institution details</h2>
            <div className="space-y-5">
              <InfoRow icon={MapPin} label="Address" value={inst.address} />
              <InfoRow icon={User} label="President / Principal" value={inst.president} testId="profile-president" />
              <InfoRow icon={Calendar} label="Year established" value={inst.year_established} />
              <InfoRow icon={Mail} label="Contact email" value={inst.email} />
              <InfoRow icon={Phone} label="Contact number" value={inst.contact} />
            </div>
          </Card>

          {/* Charts */}
          <div className="md:col-span-7 space-y-8">
            {/* Degrees */}
            <Card className="p-8 border-slate-200" data-testid="profile-degrees">
              <div className="flex items-baseline justify-between mb-6">
                <h2 className="font-display font-bold text-xl text-slate-900">Degrees issued</h2>
                <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">All-time</span>
              </div>
              {degree_breakdown.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No credentials issued yet.</p>
              ) : (
                <ul className="space-y-4">
                  {degree_breakdown.map((d) => (
                    <li key={d.degree} className="flex items-center gap-4">
                      <div className="w-40 md:w-56 shrink-0 text-sm text-slate-700 truncate">{d.degree}</div>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.max(6, (d.count / maxDegree) * 100)}%` }} />
                      </div>
                      <div className="w-10 shrink-0 text-right font-display font-extrabold text-slate-900">{d.count}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Graduates by year */}
            <Card className="p-8 border-slate-200" data-testid="profile-years">
              <div className="flex items-baseline justify-between mb-6">
                <h2 className="font-display font-bold text-xl text-slate-900">Graduating classes</h2>
                <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">Last 5 years</span>
              </div>
              {graduates_by_year.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No graduation data yet.</p>
              ) : (
                <div className="flex items-end gap-3 md:gap-4 h-40 pt-4">
                  {graduates_by_year.map(([year, count]) => (
                    <div key={year} className="flex-1 flex flex-col items-center gap-2 min-w-0" data-testid={`year-bar-${year}`}>
                      <div className="font-display font-bold text-sm text-slate-800">{count}</div>
                      <div className="w-full bg-blue-600 rounded-t-sm transition-all" style={{ height: `${Math.max(6, (count / maxYear) * 100)}%` }} />
                      <div className="text-xs text-slate-500 font-mono">{year}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900 text-white text-center">
        <div className="w-full max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Hold a credential from {inst.name}?</h2>
          <p className="mt-4 text-slate-300">Enter the Certificate ID + voucher code to authenticate instantly.</p>
          <Link to="/" className="inline-block mt-8">
            <Button size="lg" className="h-12 px-8 bg-white text-slate-900 hover:bg-slate-100 font-display font-semibold" data-testid="profile-cta-verify">
              Verify a credential <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
        <div className="text-sm text-slate-800 mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}
